﻿import {
  Component,
  JSXElement,
  createSignal,
  createEffect,
  Show,
  on,
} from 'solid-js';
import Spinner from '../Spinner/Spinner';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import { BtnClasses } from '../../types/common';

const ANIMATION_LEAD_OUT_MS = 200;
const MS_PER_SECOND = 1000;

export const SnackbarConfig = {
  colors: {
    trimbleRed: '#A51D25',
  },
  timeouts: {
    default: 5200,
    error: 4000,
  },
};

export interface ISnackbarMessage {
  buttonText?: string;
  buttonIcon?: string;
  buttonClassNames?: string;
  delay?: number;
  showSpinner?: boolean;
  text: string;
  timeoutMilliseconds?: number;
  closeOnCallback?: boolean;
  callback?(args?: unknown): void;
  onClick?(): void;
}

export interface SnackbarProps {
  color?: string;
  message?: ISnackbarMessage;
  'data-cy'?: string;
}

const Snackbar: Component<SnackbarProps> = (props): JSXElement => {
  const [visible, setVisible] = createSignal(false);
  let timer: number | undefined;

  const cancelTimeout = (): void => {
    if (timer) {
      window.clearTimeout(timer);
      timer = undefined;
    }
  };

  const hideSnackbar = (): void => {
    cancelTimeout();
    setVisible(false);
  };

  const showSnackbar = (timeout?: number): void => {
    cancelTimeout();
    setVisible(true);
    timer = window.setTimeout(
      hideSnackbar,
      timeout ?? SnackbarConfig.timeouts.default
    );
  };

  createEffect(
    on(
      () => props.message,
      (msg) => {
        if (msg) {
          setTimeout(() => {
            showSnackbar(msg.timeoutMilliseconds);
          }, msg.delay ?? 0);
        } else {
          hideSnackbar();
        }
      }
    )
  );

  const onClickMessage = (): void => {
    hideSnackbar();
    props.message?.onClick?.();
  };

  const timeout = (): number | undefined => props.message?.timeoutMilliseconds;
  const animationDelay = (): string => {
    const t = timeout();
    return t
      ? `0s, ${((t - ANIMATION_LEAD_OUT_MS) / MS_PER_SECOND).toString()}s`
      : '';
  };

  return (
    <Show when={props.message}>
      <div
        onClick={onClickMessage}
        class={`snackbar ${visible() ? 'visible' : ''} ${!props.message?.buttonText && !props.message?.buttonIcon ? 'buttonless' : ''}`}
        style={{
          ...(props.color ? { 'background-color': props.color } : {}),
          'animation-delay': visible() ? animationDelay() : '',
        }}
        data-testid={props['data-cy'] || 'snackbar'}
      >
        <div class="d-flex center">
          <Show when={props.message?.showSpinner}>
            <span class="mr-1">
              <Spinner show={true} />
            </span>
          </Show>
          <span>{props.message?.text}</span>
          <Show when={props.message?.buttonText && props.message.callback}>
            <Button
              content={props.message!.buttonText}
              color={BtnClasses.Primary}
              size="small"
              onClick={props.message!.callback}
              class="action-button"
            />
          </Show>
          <Show
            when={
              props.message?.buttonIcon &&
              (props.message.callback || props.message.closeOnCallback)
            }
          >
            <IconButton
              icon={props.message!.buttonIcon!}
              color={BtnClasses.Tertiary}
              shape="icon-circle"
              classes={`ml-2 ${props.message?.buttonClassNames ?? ''}`}
              clicked={() => {
                if (props.message?.callback) {
                  props.message.callback();
                } else if (props.message?.closeOnCallback) {
                  hideSnackbar();
                }
              }}
            />
          </Show>
        </div>
      </div>
    </Show>
  );
};

export default Snackbar;
